import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    plus: ['M12 5v14', 'M5 12h14'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    userPlus: ['M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M19 8v6', 'M16 11h6'],
    chevronRight: ['M9 18l6-6-6-6'],
    loader: ['M12 2v4', 'M12 18v4', 'M4.93 4.93l2.83 2.83', 'M16.24 16.24l2.83 2.83', 'M2 12h4', 'M18 12h4', 'M4.93 19.07l2.83-2.83', 'M16.24 7.76l2.83-2.83'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    truck: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z']
};

export default function PassengerAllocation({ user }) {
    const [allocations, setAllocations] = useState([]);
    const [clients, setClients] = useState([]);
    const [trips, setTrips] = useState([]);
    const [passengers, setPassengers] = useState([]);
    const [masterData, setMasterData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('All');
    const [modal, setModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(null);

    const operatorId = user?.operatorId || user?._id || 'all';

    useEffect(() => {
        fetchData();
        fetchCommonData();
    }, []);

    const fetchCommonData = async () => {
        try {
            const [clientRes, tripRes, passengerRes, masterRes] = await Promise.all([
                axios.get(`${API}/clients/${operatorId}`),
                axios.get(`${API}/trips/${operatorId}`),
                axios.get(`${API}/passengers/${operatorId}`),
                axios.get(`/api/master-data`)
            ]);
            setClients(clientRes.data);
            setTrips(tripRes.data);
            setPassengers(passengerRes.data);
            setMasterData(masterRes.data);
        } catch (err) { console.error(err); }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/passenger-allocations/${operatorId}`);
            setAllocations(res.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleSave = async (allocs, reconcileTripId) => {
        try {
            // 1. Reconcile deletions if editing a specific trip
            if (reconcileTripId) {
                const existingOnTrip = allocations.filter(a => (a.tripId?._id || a.tripId) === reconcileTripId);
                const keptIds = allocs.filter(a => a._id).map(a => a._id);
                const toDelete = existingOnTrip.filter(a => !keptIds.includes(a._id));
                
                if (toDelete.length > 0) {
                    await Promise.all(toDelete.map(a => axios.delete(`${API}/passenger-allocations/${a._id}`)));
                }
            }

            // 2. Create/Update remaining
            await Promise.all(allocs.map(a => {
                const payload = { ...a, operatorId };
                const sanitizeId = (id) => {
                    if (!id) return null;
                    if (typeof id === 'object') return id._id || null;
                    return id === '' ? null : id;
                };
                payload.passengerId = sanitizeId(payload.passengerId);
                payload.tripId = sanitizeId(payload.tripId);
                payload.pickupStopId = sanitizeId(payload.pickupStopId);
                payload.dropStopId = sanitizeId(payload.dropStopId);

                if (a._id) {
                    return axios.put(`${API}/passenger-allocations/${a._id}`, payload);
                }
                return axios.post(`${API}/passenger-allocations`, payload);
            }));
            
            setModal(false);
            fetchData();
        } catch (err) {
            console.error('Error saving allocation:', err);
            alert(`Failed to save: ${err.response?.data?.message || err.message}`);
        }
    };

    const handleDelete = async () => {
        try {
            if (deleteModal.tripId) {
                const targetTripId = deleteModal.tripId?._id || deleteModal.tripId;
                const toDelete = allocations.filter(a => (a.tripId?._id || a.tripId) === targetTripId);
                await Promise.all(toDelete.map(a => axios.delete(`${API}/passenger-allocations/${a._id}`)));
            } else {
                await axios.delete(`${API}/passenger-allocations/${deleteModal._id}`);
            }
            setDeleteModal(null);
            fetchData();
        } catch (err) { console.error(err); }
    };

    const groupedData = React.useMemo(() => {
        const groups = {};
        
        (trips || []).forEach(t => {
            if (t._id) {
                groups[t._id] = {
                    trip: t,
                    allocations: []
                };
            }
        });

        (allocations || []).forEach(a => {
            const tripId = a.tripId?._id || a.tripId;
            if (!tripId) return;
            if (!groups[tripId]) {
                groups[tripId] = {
                    trip: a.tripId,
                    allocations: []
                };
            }
            groups[tripId].allocations.push(a);
        });
        
        return Object.values(groups).filter(g => {
            if (filterType === 'Allocated' && g.allocations.length === 0) return false;
            if (filterType === 'Not Allocated' && g.allocations.length > 0) return false;

            const tName = `${g.trip?.tripName || ''} ${g.trip?.routeId?.name || ''}`.toLowerCase();
            const pNames = g.allocations.map(a => a.passengerId?.name?.toLowerCase() || '').join(' ');
            const cName = g.trip?.clientId?.companyName?.toLowerCase() || '';
            const query = search.toLowerCase();
            return tName.includes(query) || pNames.includes(query) || cName.includes(query);
        });
    }, [allocations, trips, search, filterType]);

    const handleEditTrip = (group) => {
        setModal(group.allocations[0] || { tripId: group.trip });
    };

    return (
        <div className="md-page">
            <style>{`
                .md-page { padding: 40px; background: #f8fafc; min-height: 100vh; font-family: 'Outfit', sans-serif; }
                .md-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 32px; }
                .md-title { font-size: 32px; font-weight: 800; color: #0f172a; margin: 0; letter-spacing: -0.02em; }
                .md-subtitle { color: #64748b; margin-top: 8px; font-size: 16px; }
                
                .md-add-btn {
                    background: #16539C; color: white; border: none; padding: 12px 24px;
                    border-radius: 12px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 10px;
                    transition: all 0.3s; box-shadow: 0 4px 15px rgba(22, 83, 156, 0.25);
                }
                .md-add-btn:hover { background: #11427D; transform: translateY(-2px); box-shadow: 0 8px 25px rgba(22, 83, 156, 0.3); }

                .md-panel { background: white; border-radius: 28px; border: 1px solid #e2e8f0; box-shadow: 0 4px 30px rgba(0,0,0,0.02); overflow: hidden; }
                .md-panel-toolbar { padding: 24px 32px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; background: #fff; }
                
                .md-search-wrap { position: relative; flex: 1; max-width: 450px; }
                .md-search-wrap svg { position: absolute; left: 18px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
                .md-input.md-search { padding: 14px 18px 14px 48px; border-radius: 18px; background: #f8fafc; border: 1.5px solid #e2e8f0; width: 100%; transition: all 0.3s; outline: none; font-size: 14px; }
                .md-input.md-search:focus { border-color: #16539C; background: white; box-shadow: 0 0 0 5px rgba(22, 83, 156, 0.08); }

                .md-table { width: 100%; border-collapse: separate; border-spacing: 0; }
                .md-th { padding: 20px 32px; text-align: left; font-size: 11px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 0.1em; border-bottom: 1px solid #f1f5f9; background: #f8fafc; }
                .md-td { padding: 24px 32px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; color: #334155; font-size: 14px; }
                .md-tr:hover { background: #fbfcfe; }
                .md-tr:last-child .md-td { border-bottom: none; }

                .passenger-avatars { display: flex; align-items: center; }
                .avatar-stack { width: 32px; height: 32px; border-radius: 10px; border: 2px solid white; background: #16539C; color: white; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 800; margin-left: -10px; transition: transform 0.2s; }
                .avatar-stack:first-child { margin-left: 0; }
                .avatar-stack:hover { transform: translateY(-4px); z-index: 10; }
                .avatar-more { background: #e2e8f0; color: #475569; }

                .trip-badge { font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 8px; background: #f1f5f9; color: #475569; display: inline-flex; align-items: center; gap: 4px; }
                .trip-type-login { background: #ecfdf5; color: #059669; }
                .trip-type-logout { background: #fff7ed; color: #c2410c; }

                .md-status-badge { padding: 6px 14px; border-radius: 99px; font-size: 12px; font-weight: 700; }
                .md-badge-active { background: #ecfdf5; color: #059669; }

                .md-icon-btn { width: 40px; height: 40px; border-radius: 12px; border: 1.5px solid #f1f5f9; background: white; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; color: #64748b; }
                .md-icon-btn:hover { border-color: #16539C; color: #16539C; background: #f0f7ff; transform: translateY(-3px); box-shadow: 0 4px 12px rgba(22, 83, 156, 0.15); }
                .md-icon-btn-danger:hover { border-color: #ef4444; color: #ef4444; background: #fef2f2; box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15); }

                /* Wizard Styles */
                .wizard-container { display: grid; grid-template-columns: 350px 1fr; gap: 0; height: 80vh; max-height: 80vh; min-height: 0; }
                .wizard-sidebar { padding: 32px; border-right: 1px solid #f1f5f9; background: #fafbfc; overflow-y: auto; }
                .wizard-content { padding: 32px; display: flex; flex-direction: column; background: white; overflow: hidden; height: 100%; }
                
                .wizard-step { margin-bottom: 24px; }
                .wizard-label { display: block; font-size: 14px; font-weight: 700; color: #1e293b; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
                .wizard-label span { width: 22px; height: 22px; background: #16539C; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; }
                
                .passenger-list-card { border: 1.5px solid #e2e8f0; border-radius: 16px; overflow: hidden; background: white; }
                .passenger-search { padding: 12px 16px; border-bottom: 1px solid #f1f5f9; background: #f8fafc; display: flex; align-items: center; gap: 10px; }
                .passenger-scroll { max-height: 300px; overflow-y: auto; }
                .passenger-row { padding: 10px 16px; display: flex; align-items: center; gap: 12px; border-bottom: 1px solid #f1f5f9; cursor: pointer; transition: all 0.2s; }
                .passenger-row:hover { background: #f0f7ff; }
                .passenger-row.selected { background: #f0efff; }
                
                .capacity-bar { height: 8px; background: #e2e8f0; border-radius: 8px; overflow: hidden; margin-top: 8px; width: 120px; }
                .capacity-progress { height: 100%; transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
                
                .stop-selector { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
                .trip-dropdown-item:hover { background: #f8fafc !important; }
            `}</style>

            <div className="md-header">
                <div>
                    <h1 className="md-title">Passenger Allocation</h1>
                    <p className="md-subtitle">Manage and monitor passenger distribution across your fleet.</p>
                </div>
                <button className="md-add-btn" onClick={() => setModal(true)}>
                    <Icon d={icons.userPlus} size={18} /> New Allocation
                </button>
            </div>

            <div className="md-panel">
                <div className="md-panel-toolbar">
                    <div style={{ display: 'flex', gap: '15px', flex: 1, maxWidth: '650px' }}>
                        <div className="md-search-wrap">
                            <Icon d={icons.search} size={16} />
                            <input
                                className="md-input md-search"
                                placeholder="Search by trip, client or passenger..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                        <select 
                            className="md-input" 
                            style={{ width: '200px', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '18px', padding: '14px 18px', outline: 'none', transition: 'all 0.3s' }}
                            value={filterType}
                            onChange={e => setFilterType(e.target.value)}
                        >
                            <option value="All">All Allocations</option>
                            <option value="Allocated">Allocated</option>
                            <option value="Not Allocated">Not Allocated</option>
                        </select>
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 100, textAlign: 'center', color: '#94a3b8' }}>
                        <div style={{ marginBottom: 16 }}><Icon d={icons.loader} size={32} className="spinning" /></div>
                        Loading allocations...
                    </div>
                ) : (
                    <div className="md-table-wrap">
                        <table className="md-table">
                            <thead>
                                <tr>
                                    <th className="md-th">Trip Details</th>
                                    <th className="md-th">Client</th>
                                    <th className="md-th">Vehicle & Driver</th>
                                    <th className="md-th">Occupancy</th>
                                    <th className="md-th">Allocated Passengers</th>
                                    <th className="md-th" style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedData.map(g => {
                                    const capacity = g.trip?.vehicleId?.seatingCapacity || 0;
                                    const count = g.allocations.length;
                                    const percent = capacity > 0 ? (count / capacity) * 100 : 0;
                                    const color = percent > 100 ? '#ef4444' : (percent > 80 ? '#f59e0b' : '#16539C');

                                    return (
                                        <tr key={g.trip?._id} className="md-tr">
                                            <td className="md-td">
                                                {count === 0 && (
                                                    <div style={{ marginBottom: 4 }}>
                                                        <span style={{ background: '#fef2f2', color: '#ef4444', padding: '3px 6px', borderRadius: '4px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                            Not Allocated
                                                        </span>
                                                    </div>
                                                )}
                                                <div style={{ fontWeight: 800, color: '#0f172a', fontSize: 16 }}>
                                                    {g.trip?.tripName || 'Unnamed Trip'} <span style={{ fontWeight: 500, color: '#64748b', fontSize: 13 }}>({g.trip?.routeId?.name || 'No Route'})</span>
                                                </div>
                                                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                                                    <span className={`trip-badge ${g.trip?.tripType === 'Login' ? 'trip-type-login' : 'trip-type-logout'}`}>
                                                        {g.trip?.tripType}
                                                    </span>
                                                    <span className="trip-badge">
                                                        {g.trip?.startTime || g.trip?.pickupTime || '—'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="md-td">
                                                <div style={{ fontWeight: 700, color: '#475569' }}>{g.trip?.clientId?.companyName || '—'}</div>
                                            </td>
                                            <td className="md-td">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <div style={{ width: 32, height: 32, borderRadius: 8, background: '#fef3c7', color: '#92400e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Icon d={icons.truck} size={16} />
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: '#1e293b' }}>{g.trip?.vehicleId?.plateNumber || 'No Vehicle'}</div>
                                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{g.trip?.vehicleId?.make} {g.trip?.vehicleId?.model}</div>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                                                    <div style={{ width: 32, height: 32, borderRadius: 8, background: '#e0f2fe', color: '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Icon d={icons.user} size={16} />
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 600, color: '#475569', fontSize: 13 }}>{g.trip?.driverId?.name || 'No Driver'}</div>
                                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{g.trip?.driverId?.phoneNumber}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="md-td">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <div style={{ fontWeight: 800, color: color, fontSize: 16 }}>{count}<span style={{ color: '#94a3b8', fontSize: 12, fontWeight: 500 }}>/{capacity || '∞'}</span></div>
                                                    <div className="capacity-bar">
                                                        <div className="capacity-progress" style={{ width: `${Math.min(100, percent)}%`, background: color }} />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="md-td">
                                                <div className="passenger-avatars">
                                                    {g.allocations.slice(0, 5).map((a, i) => (
                                                        <div key={a._id} className="avatar-stack" title={a.passengerId?.name}>
                                                            {a.passengerId?.name?.charAt(0)}
                                                        </div>
                                                    ))}
                                                    {g.allocations.length > 5 && (
                                                        <div className="avatar-stack avatar-more">
                                                            +{g.allocations.length - 5}
                                                        </div>
                                                    )}
                                                    <div style={{ marginLeft: 12, fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                                                        {g.allocations.length} passengers
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="md-td">
                                                <div className="action-btns" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                    <button className="md-icon-btn" onClick={() => handleEditTrip(g)} title="Manage Trip Allocation">
                                                        <Icon d={icons.edit} size={18} />
                                                    </button>
                                                    <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal(g.allocations[0])} title="Clear All">
                                                        <Icon d={icons.trash} size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {modal && (
                <AllocationForm
                    clients={clients}
                    trips={trips}
                    passengers={passengers}
                    masterData={masterData}
                    allocations={allocations}
                    onClose={() => setModal(false)}
                    onSave={handleSave}
                    operatorId={operatorId}
                    editData={typeof modal === 'object' ? modal : null}
                />
            )}

            {deleteModal && (
                <div className="md-modal-overlay" onClick={() => setDeleteModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 400, borderRadius: 24, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                        <div style={{ padding: 32, textAlign: 'center' }}>
                            <div style={{ width: 64, height: 64, borderRadius: 20, background: '#fef2f2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                                <Icon d={icons.trash} size={32} />
                            </div>
                            <h3 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>Confirm Removal</h3>
                            <p style={{ color: '#64748b', margin: 0 }}>
                                {deleteModal.tripId ? (
                                    <>Clear all passenger allocations for <strong>{deleteModal.tripId?.tripName || deleteModal.tripId?.routeId?.name || 'this trip'}</strong>?</>
                                ) : (
                                    <>Remove <strong>{deleteModal.passengerId?.name}</strong> from this trip allocation?</>
                                )}
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: 12, padding: '0 32px 32px' }}>
                            <button className="md-cancel-btn" style={{ flex: 1, padding: 14, borderRadius: 14 }} onClick={() => setDeleteModal(null)}>Cancel</button>
                            <button className="md-save-btn" style={{ flex: 1, padding: 14, borderRadius: 14, background: '#ef4444' }} onClick={handleDelete}>Remove All</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function AllocationForm({ clients, trips, passengers, masterData, allocations, onClose, onSave, operatorId, editData }) {
    const [selectedClient, setSelectedClient] = useState(editData?.tripId?.clientId?._id || editData?.tripId?.clientId || '');
    const [selectedTrip, setSelectedTrip] = useState(editData?.tripId?._id || editData?.tripId || '');
    const [selectedPassengers, setSelectedPassengers] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [routeStops, setRouteStops] = useState([]);
    const [pSearch, setPSearch] = useState('');
    const [loadingStops, setLoadingStops] = useState(false);
    const [tripDropdownOpen, setTripDropdownOpen] = useState(false);

    // Load existing allocations for the selected trip if editing
    useEffect(() => {
        if (editData && selectedTrip) {
            const existing = allocations.filter(a => (a.tripId?._id || a.tripId) === selectedTrip);
            setSelectedPassengers(existing.map(a => a.passengerId?._id || a.passengerId));
            setAssignments(existing.map(a => ({
                passengerId: a.passengerId?._id || a.passengerId,
                name: a.passengerId?.name,
                pickupStopId: a.pickupStopId?._id || a.pickupStopId || '',
                dropStopId: a.dropStopId?._id || a.dropStopId || '',
                _id: a._id,
                tripId: a.tripId?._id || a.tripId
            })));
        }
    }, [editData, selectedTrip]);

    // Derived: Selected Client Object
    const clientObj = clients.find(c => c._id === selectedClient);
    
    // Derived: Passengers for this client
    const clientPassengers = React.useMemo(() => {
        if (!selectedClient) return [];
        return (passengers || []).filter(p => (p.clientId?._id || p.clientId) === selectedClient);
    }, [selectedClient, passengers]);

    // Derived: Trips for this client
    const clientTrips = trips.filter(t => (t.clientId?._id || t.clientId) === selectedClient);

    // Derived: Selected Trip Object & Capacity
    const tripObj = trips.find(t => t._id === selectedTrip);
    const capacity = React.useMemo(() => {
        if (!tripObj) return 0;
        // 1. Prioritize specific vehicle's capacity if defined
        if (tripObj.vehicleId?.seatingCapacity > 0) return tripObj.vehicleId.seatingCapacity;
        // 2. Fallback to Master Data for the vehicle type
        const vType = tripObj.vehicleId?.vehicleType || tripObj.vehicleType;
        const master = masterData.find(m => m.category === 'vehicle-types' && m.name === vType);
        return master?.seatingCapacity || 0;
    }, [tripObj, masterData]);

    // Current Count of allocated passengers for this trip (excluding the ones we are currently managing in the wizard)
    const alreadyAllocatedCount = React.useMemo(() => {
        if (!selectedTrip) return 0;
        // If we are editing THIS trip, we assume we are managing all its allocations, so "other" allocations is 0
        if (editData && (editData.tripId?._id || editData.tripId) === selectedTrip) return 0;
        return (allocations || []).filter(a => (a.tripId?._id || a.tripId) === selectedTrip).length;
    }, [selectedTrip, allocations, editData]);

    const totalSelected = selectedPassengers.length;
    const totalOccupancy = alreadyAllocatedCount + totalSelected;
    const isOverCapacity = capacity > 0 && totalOccupancy > capacity;

    useEffect(() => {
        if (selectedTrip) {
            const trip = trips.find(t => t._id === selectedTrip);
            if (trip?.routeId) fetchRouteStops(trip.routeId._id || trip.routeId);
        }
    }, [selectedTrip]);

    const fetchRouteStops = async (routeId) => {
        setLoadingStops(true);
        try {
            const res = await axios.get(`${API}/route-stops/${routeId}`);
            setRouteStops(res.data);
        } catch (err) { console.error(err); }
        finally { setLoadingStops(false); }
    };

    const togglePassenger = (pid) => {
        setSelectedPassengers(prev => {
            if (prev.includes(pid)) return prev.filter(id => id !== pid);
            if (capacity > 0 && (alreadyAllocatedCount + prev.length) >= capacity) {
                alert(`Vehicle capacity of ${capacity} reached!`);
                return prev;
            }
            return [...prev, pid];
        });
    };

    const selectAllFiltered = () => {
        const filteredPids = clientPassengers.filter(p => 
            p.name.toLowerCase().includes(pSearch.toLowerCase()) || 
            p.email.toLowerCase().includes(pSearch.toLowerCase())
        ).map(p => p._id);
        
        let newSelection = [...selectedPassengers];
        for (const pid of filteredPids) {
            if (!newSelection.includes(pid)) {
                if (capacity > 0 && (alreadyAllocatedCount + newSelection.length) >= capacity) break;
                newSelection.push(pid);
            }
        }
        setSelectedPassengers(newSelection);
    };

    useEffect(() => {
        setAssignments(prev => {
            return selectedPassengers.map(pid => {
                const existing = prev.find(a => a.passengerId === pid);
                if (existing) return existing;
                
                const p = clientPassengers.find(x => x._id === pid);
                
                // Fallback name lookup: check the global allocations list or the master passengers list
                const name = p?.name || 
                             allocations.find(a => (a.passengerId?._id || a.passengerId) === pid)?.passengerId?.name || 
                             passengers.find(x => x._id === pid)?.name || 
                             'Unknown';

                // Find if this was an existing allocation for this trip to restore stops
                const existingAlloc = (allocations || []).find(a => 
                    (a.tripId?._id || a.tripId) === selectedTrip && 
                    (a.passengerId?._id || a.passengerId) === pid
                );

                return {
                    passengerId: pid,
                    name,
                    pickupStopId: existingAlloc?.pickupStopId?._id || existingAlloc?.pickupStopId || '',
                    dropStopId: existingAlloc?.dropStopId?._id || existingAlloc?.dropStopId || '',
                    _id: existingAlloc?._id,
                    passengerInfo: p?.isContact ? p : undefined
                };
            });
        });
    }, [selectedPassengers, clientPassengers, allocations, selectedTrip, passengers]);

    const handleAssignmentChange = (pid, field, value) => {
        setAssignments(prev => prev.map(a => a.passengerId === pid ? { ...a, [field]: value } : a));
    };
    const handleSubmit = () => {
        const final = assignments.map(a => {
            let pPoint = '';
            if (a.pickupStopId) {
                const s = routeStops.find(rs => rs._id === a.pickupStopId);
                pPoint = s?.name || s?.stopName || '';
            } else {
                pPoint = tripObj?.routeId?.pickupLocation || 'Route Origin';
            }

            let dPoint = '';
            if (a.dropStopId) {
                const s = routeStops.find(rs => rs._id === a.dropStopId);
                dPoint = s?.name || s?.stopName || '';
            } else {
                dPoint = tripObj?.routeId?.dropLocation || 'Route Destination';
            }

            return {
                ...a,
                pickupPoint: pPoint,
                dropPoint: dPoint,
                tripId: selectedTrip,
                allocationType: tripObj?.tripFrequency || 'Daily'
            };
        });
        // Pass the tripId as second argument to trigger reconciliation (deletion of unselected ones)
        onSave(final, selectedTrip);
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" style={{ maxWidth: 1200, width: '95%', borderRadius: 28, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                <div className="wizard-container">
                    {/* Left Panel: Configuration */}
                    <div className="wizard-sidebar">
                        <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 24px' }}>Allocation Wizard</h2>
                        
                        <div className="wizard-step">
                            <label className="wizard-label"><span>1</span> Select Client</label>
                            <select 
                                className="md-input" 
                                value={selectedClient} 
                                disabled={!!editData}
                                onChange={e => { setSelectedClient(e.target.value); setSelectedTrip(''); setSelectedPassengers([]); setTripDropdownOpen(false); }}
                            >
                                <option value="">Choose Client...</option>
                                {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                            </select>
                        </div>

                        <div className="wizard-step" style={{ position: 'relative' }}>
                            <label className="wizard-label"><span>2</span> Select Trip</label>
                            
                            <div style={{ position: 'relative' }}>
                                <button
                                    type="button"
                                    onClick={() => selectedClient && setTripDropdownOpen(!tripDropdownOpen)}
                                    disabled={!selectedClient}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        border: '1.5px solid #e2e8f0',
                                        background: selectedClient ? 'white' : '#f1f5f9',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: selectedClient ? 'pointer' : 'not-allowed',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#1e293b',
                                        textAlign: 'left',
                                        transition: 'all 0.2s',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                    }}
                                >
                                    {tripObj ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                                            <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', fontWeight: 800 }}>
                                                {tripObj.tripName || 'Unnamed'}
                                            </span>
                                            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
                                                ({tripObj.routeId?.name || 'No Route'})
                                            </span>
                                            <span className={`trip-badge ${tripObj.tripType === 'Login' ? 'trip-type-login' : 'trip-type-logout'}`} style={{ marginLeft: 'auto', marginRight: 4, flexShrink: 0 }}>
                                                {tripObj.tripType}
                                            </span>
                                        </div>
                                    ) : (
                                        <span style={{ color: '#94a3b8' }}>Choose Trip...</span>
                                    )}
                                    <span style={{ transform: tripDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'inline-block', lineHeight: 1, color: '#64748b', fontSize: '10px' }}>▼</span>
                                </button>

                                {tripDropdownOpen && selectedClient && (
                                    <>
                                        {/* Backdrop to close click */}
                                        <div 
                                            onClick={() => setTripDropdownOpen(false)} 
                                            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}
                                        />
                                        <div style={{
                                            position: 'absolute',
                                            top: 'calc(100% + 6px)',
                                            left: 0,
                                            right: 0,
                                            background: 'white',
                                            border: '1.5px solid #e2e8f0',
                                            borderRadius: '16px',
                                            boxShadow: '0 12px 30px rgba(0,0,0,0.1)',
                                            zIndex: 10000,
                                            maxHeight: '250px',
                                            overflowY: 'auto',
                                            padding: '6px'
                                        }}>
                                            {clientTrips.length === 0 ? (
                                                <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No trips available</div>
                                            ) : (
                                                clientTrips.map(t => {
                                                    const isSelected = t._id === selectedTrip;
                                                    return (
                                                        <div
                                                            key={t._id}
                                                            onClick={() => {
                                                                setSelectedTrip(t._id);
                                                                setSelectedPassengers([]);
                                                                setTripDropdownOpen(false);
                                                            }}
                                                            style={{
                                                                padding: '10px 12px',
                                                                borderRadius: '10px',
                                                                cursor: 'pointer',
                                                                background: isSelected ? '#f0f7ff' : 'transparent',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                gap: 8,
                                                                transition: 'all 0.15s',
                                                                marginBottom: '2px'
                                                            }}
                                                            className="trip-dropdown-item"
                                                        >
                                                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                                                <span style={{ fontSize: '13px', fontWeight: 800, color: isSelected ? '#16539C' : '#1e293b', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                    {t.tripName || 'Unnamed'}
                                                                </span>
                                                                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                                                    Route: {t.routeId?.name || 'No Route'}
                                                                </span>
                                                            </div>
                                                            <span className={`trip-badge ${t.tripType === 'Login' ? 'trip-type-login' : 'trip-type-logout'}`} style={{ flexShrink: 0 }}>
                                                                {t.tripType}
                                                            </span>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                            
                            {tripObj && (
                                <div style={{ marginTop: 12, padding: 16, background: '#f1f5f9', borderRadius: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                                        <span>Trip Capacity</span>
                                        <span style={{ color: isOverCapacity ? '#ef4444' : '#16539C' }}>
                                            {totalOccupancy} / {capacity || '∞'}
                                        </span>
                                    </div>
                                    <div className="capacity-bar">
                                        <div 
                                            className="capacity-progress" 
                                            style={{ 
                                                width: `${Math.min(100, (totalOccupancy / (capacity || 1)) * 100)}%`,
                                                background: isOverCapacity ? '#ef4444' : (totalOccupancy > capacity * 0.8 ? '#f59e0b' : '#16539C')
                                            }} 
                                        />
                                    </div>
                                    <p style={{ fontSize: 11, color: '#94a3b8', margin: '8px 0 0' }}>
                                        {alreadyAllocatedCount} already assigned + {totalSelected} newly selected
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="wizard-step">
                            <label className="wizard-label"><span>3</span> Passengers ({totalSelected})</label>
                            <div className="passenger-list-card">
                                <div className="passenger-search">
                                    <Icon d={icons.search} size={14} />
                                    <input 
                                        placeholder="Search people..." 
                                        value={pSearch} 
                                        onChange={e => setPSearch(e.target.value)}
                                        style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, flex: 1 }}
                                        disabled={!selectedTrip}
                                    />
                                    {selectedTrip && <button onClick={selectAllFiltered} style={{ background: 'none', border: 'none', color: '#16539C', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>Select All</button>}
                                </div>
                                <div className="passenger-scroll">
                                    {!selectedTrip ? (
                                        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Select a trip first</div>
                                    ) : clientPassengers.length === 0 ? (
                                        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No passengers found</div>
                                    ) : (
                                        clientPassengers.filter(p => p.name.toLowerCase().includes(pSearch.toLowerCase())).map(p => (
                                            <div 
                                                key={p._id} 
                                                className={`passenger-row ${selectedPassengers.includes(p._id) ? 'selected' : ''}`}
                                                onClick={() => togglePassenger(p._id)}
                                            >
                                                <input type="checkbox" checked={selectedPassengers.includes(p._id)} readOnly />
                                                <div style={{ fontSize: 13 }}>
                                                    <div style={{ fontWeight: 600 }}>{p.name}</div>
                                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.employeeId}</div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Panel: Detail Mapping */}
                    <div className="wizard-content">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Step 4: Stop Assignments</h3>
                            <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 50, cursor: 'pointer' }}><Icon d={icons.x} size={16} /></button>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {assignments.length === 0 ? (
                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                    <div style={{ fontSize: 48, marginBottom: 16 }}>🚌</div>
                                    <p>Select passengers to configure their pickup and drop points.</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    {assignments.map(a => (
                                        <div key={a.passengerId} style={{ padding: 20, border: '1.5px solid #e2e8f0', borderRadius: 20, background: '#fafbfc' }}>
                                            <div style={{ fontWeight: 800, marginBottom: 12, color: '#1e293b' }}>{a.name}</div>
                                            <div className="stop-selector">
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Pickup Stop</label>
                                                    <select 
                                                        className="md-input" 
                                                        value={a.pickupStopId} 
                                                        onChange={e => handleAssignmentChange(a.passengerId, 'pickupStopId', e.target.value)}
                                                    >
                                                        <option value="">Start: {tripObj?.routeId?.pickupLocation || 'Route Origin'}</option>
                                                        {routeStops.map(s => <option key={s._id} value={s._id}>{s.name || s.stopName}</option>)}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Drop Stop</label>
                                                    <select 
                                                        className="md-input" 
                                                        value={a.dropStopId} 
                                                        onChange={e => handleAssignmentChange(a.passengerId, 'dropStopId', e.target.value)}
                                                    >
                                                        <option value="">End: {tripObj?.routeId?.dropLocation || 'Route Destination'}</option>
                                                        {routeStops.map(s => <option key={s._id} value={s._id}>{s.name || s.stopName}</option>)}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                            <button className="md-cancel-btn" style={{ padding: '12px 24px', borderRadius: 12 }} onClick={onClose}>Discard</button>
                            <button 
                                className="md-add-btn" 
                                disabled={!selectedTrip || assignments.length === 0 || isOverCapacity}
                                onClick={handleSubmit}
                                style={{ padding: '12px 32px' }}
                            >
                                Confirm {assignments.length} Allocation{assignments.length !== 1 ? 's' : ''}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

